import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ActingOrgProvider } from './context/ActingOrgContext';
import { BrandingProvider } from './context/BrandingContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import SchoolsPage from './pages/SchoolsPage';
import SchoolYearsPage from './pages/SchoolYearsPage';
import ClassListPage from './pages/ClassListPage';
import StudentListPage from './pages/StudentListPage';
import QuestionnairesPage from './pages/QuestionnairesPage';
import QuestionnaireTrashPage from './pages/QuestionnaireTrashPage';
import QuestionnaireDetailPage from './pages/QuestionnaireDetailPage';
import QuestionEditPage from './pages/QuestionEditPage';
import QuestionnairePreviewPage from './pages/QuestionnairePreviewPage';
import QuestionnairePushPage from './pages/QuestionnairePushPage';
import IndicatorConfigPage from './pages/IndicatorConfigPage';
import StatisticsPage from './pages/StatisticsPage';
import SiteStatisticsPage from './pages/SiteStatisticsPage';
import QuestionBanksPage from './pages/QuestionBanksPage';
import QuestionBankDetailPage from './pages/QuestionBankDetailPage';
import BankQuestionEditPage from './pages/BankQuestionEditPage';
import TeacherListPage from './pages/TeacherListPage';
import SchoolDataPage from './pages/SchoolDataPage';
import DataExportPage from './pages/DataExportPage';
import UserSearchPage from './pages/UserSearchPage';
import StudentDetailPage from './pages/StudentDetailPage';
import CrisisMonitorPage from './pages/CrisisMonitorPage';
import ReportConfigPage from './pages/ReportConfigPage';
import ReportViewPage from './pages/ReportViewPage';
import HelpPage from './pages/HelpPage';
import OrgManagementPage from './pages/OrgManagementPage';

const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, isAdmin } = useAuth();

  // Also check localStorage token directly as a fallback
  const hasToken = !!localStorage.getItem('admin_token');

  if (!isAuthenticated || !isAdmin) {
    // If we have a token but user state is not loaded yet, wait
    if (hasToken) {
      return <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">加载中...</div>
      </div>;
    }
    return <Navigate to="/login" replace />;
  }

  return children;
};

const SuperAdminRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, isSuperAdmin } = useAuth();

  const hasToken = !!localStorage.getItem('admin_token');

  if (!isAuthenticated) {
    if (hasToken) {
      return <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">加载中...</div>
      </div>;
    }
    return <Navigate to="/login" replace />;
  }

  // 非超级管理员重定向回仪表盘
  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ToastProvider>
        <ActingOrgProvider>
          <BrandingProvider>
        <Router basename={process.env.PUBLIC_URL || '/'}>
          <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="user-search" element={<UserSearchPage />} />
            <Route path="user-search/students/:studentId" element={<StudentDetailPage />} />
            <Route path="crisis-monitor" element={<CrisisMonitorPage />} />
            <Route path="schools" element={<SchoolsPage />} />
            <Route path="schools/:schoolId/years" element={<SchoolYearsPage />} />
            <Route path="schools/:schoolId/years/:year/classes" element={<ClassListPage />} />
            <Route path="schools/:schoolId/years/:year/classes/:classId/students" element={<StudentListPage />} />
            <Route path="schools/:schoolId/teachers" element={<TeacherListPage />} />
            <Route path="schools/:schoolId/data" element={<SchoolDataPage />} />
            <Route path="questionnaires" element={<QuestionnairesPage />} />
            <Route path="questionnaires/trash" element={<QuestionnaireTrashPage />} />
            <Route path="questionnaires/:id" element={<QuestionnaireDetailPage />} />
            <Route path="questionnaires/:id/push" element={<QuestionnairePushPage />} />
            <Route path="questionnaires/:id/indicators" element={<IndicatorConfigPage />} />
            <Route path="questionnaires/:id/statistics" element={<StatisticsPage />} />
            <Route path="questionnaires/:id/report" element={<ReportConfigPage />} />
            <Route path="questionnaires/:id/report/view" element={<ReportViewPage />} />
            <Route path="site-statistics" element={<SiteStatisticsPage />} />
            <Route path="questionnaires/:questionnaireId/questions/:questionId/edit" element={<QuestionEditPage />} />
            <Route path="questionnaires/:questionnaireId/preview" element={<QuestionnairePreviewPage />} />
            <Route path="export" element={<DataExportPage />} />
            <Route path="question-banks" element={<QuestionBanksPage />} />
            <Route path="question-banks/:bankId" element={<QuestionBankDetailPage />} />
            <Route path="question-banks/:bankId/questions/:questionId/edit" element={<BankQuestionEditPage />} />
            <Route path="help" element={<HelpPage />} />
            <Route path="org-management" element={<SuperAdminRoute><OrgManagementPage /></SuperAdminRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
          </BrandingProvider>
        </ActingOrgProvider>
      </ToastProvider>
    </AuthProvider>
  );
};

export default App;
